import axios from 'axios';
import { API_BASE } from './config';

export const ACCESS_TOKEN_KEY = 'makaya_access_token';

const api = axios.create({
  baseURL: API_BASE,
});

const publicGetCache = new Map();
const DEFAULT_PUBLIC_CACHE_TTL_MS = 60000;

function stableStringify(value) {
  if (!value || typeof value !== 'object') return '';
  const sorted = Object.keys(value)
    .filter((key) => value[key] !== undefined && value[key] !== null && value[key] !== '')
    .sort()
    .reduce((acc, key) => {
      acc[key] = value[key];
      return acc;
    }, {});
  return JSON.stringify(sorted);
}

function clearPublicCache(prefix = '') {
  for (const key of publicGetCache.keys()) {
    if (!prefix || key.startsWith(prefix)) {
      publicGetCache.delete(key);
    }
  }
}

function cachedGet(path, options = {}, ttlMs = DEFAULT_PUBLIC_CACHE_TTL_MS) {
  const key = `${path}:${stableStringify(options.params || {})}`;
  const nowMs = Date.now();
  const cached = publicGetCache.get(key);

  if (cached?.promise) {
    return cached.promise;
  }
  if (cached?.response && cached.expiresAt > nowMs) {
    return Promise.resolve(cached.response);
  }

  const promise = api.get(path, options)
    .then((response) => {
      publicGetCache.set(key, {
        response,
        expiresAt: Date.now() + ttlMs,
      });
      return response;
    })
    .catch((error) => {
      publicGetCache.delete(key);
      throw error;
    });

  publicGetCache.set(key, {
    promise,
    expiresAt: nowMs + ttlMs,
  });
  return promise;
}

api.interceptors.request.use((config) => {
  const token = typeof window !== 'undefined' ? window.localStorage.getItem(ACCESS_TOKEN_KEY) : null;
  config.headers = config.headers || {};

  const method = String(config.method || 'get').toLowerCase();
  const hasBody = method === 'post' || method === 'put' || method === 'patch';
  const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
  if (hasBody && !config.headers['Content-Type'] && !isFormData) {
    config.headers['Content-Type'] = 'application/json';
  }

  if (token) {
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data, { withCredentials: true }),
  register: (data) => api.post('/auth/register', data, { withCredentials: true }),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  logout: () => api.post('/auth/logout', null, { withCredentials: true }),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh', null, { withCredentials: true }),
};

// Articles
export const articlesAPI = {
  list: (params) => api.get('/articles', { params }),
  featured: () => cachedGet('/articles/featured', {}, 30000),
  breaking: () => cachedGet('/articles/breaking', {}, 20000),
  latest: (limit) => cachedGet('/articles/latest', { params: { limit } }, 30000),
  popular: (limit) => cachedGet('/articles/popular', { params: { limit } }, 60000),
  byCategory: (slug, params) => api.get(`/articles/by-category/${slug}`, { params }),
  getBySlug: (slug) => api.get(`/articles/${slug}`),
  search: (q, page) => api.get('/search', { params: { q, page } }),
};

// Categories
export const categoriesAPI = {
  list: () => cachedGet('/categories', {}, 300000),
  get: (slug) => cachedGet(`/categories/${slug}`, {}, 300000),
};

// Comments
export const commentsAPI = {
  list: (articleId) => api.get(`/comments/${articleId}`),
  create: (data) => api.post('/comments', data),
};

// Plans
export const plansAPI = {
  list: () => api.get('/plans'),
};

// Subscriptions
export const subscriptionsAPI = {
  checkout: (data) => api.post('/subscriptions/checkout', data),
  status: (sessionId) => api.get(`/subscriptions/status/${sessionId}`),
  portal: (data) => api.post('/subscriptions/portal', data),
};

// Push notifications
export const pushAPI = {
  config: () => api.get('/push/config'),
  me: () => api.get('/push/me'),
  subscribe: (data) => api.post('/push/subscribe', data),
  unsubscribe: (data) => api.post('/push/unsubscribe', data),
  preferences: (data) => api.put('/push/preferences', data),
  notifications: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Polls
export const pollsAPI = {
  active: () => cachedGet('/polls/active', {}, 30000),
  vote: async (pollId, optionIndex) => {
    const response = await api.post(`/polls/${pollId}/vote?option_index=${optionIndex}`);
    clearPublicCache('/polls/active');
    return response;
  },
};

// Radio
export const radioAPI = {
  get: (params) => cachedGet('/radio', { params }, 120000),
};

function clearRadioCacheAfter(promise) {
  return promise.then((response) => {
    clearPublicCache('/radio');
    return response;
  });
}

// Sections
export const sectionsAPI = {
  get: () => cachedGet('/sections', {}, 300000),
};

// Partners
export const partnersAPI = {
  list: () => cachedGet('/partners', {}, 300000),
  media: (partnerId) => api.get(`/partners/${partnerId}/media`),
};

// Banners
export const bannersAPI = {
  list: (slot) => cachedGet('/banners', { params: { slot } }, 60000),
  impression: (bannerId) => api.post(`/banners/${bannerId}/impression`),
  click: (bannerId) => api.post(`/banners/${bannerId}/click`),
};

// Newsletter
export const newsletterAPI = {
  subscribe: (data) => api.post('/newsletter/subscribe', data),
};

// Contact
export const contactAPI = {
  send: (data) => api.post('/contact', data),
};

// Public site settings
export const siteAPI = {
  settings: () => cachedGet('/settings/public', {}, 300000),
};

// Citizen Reports
export const citizenReportsAPI = {
  submit: (data) => api.post('/citizen-reports', data),
};

// Admin
export const adminAPI = {
  dashboard: () => api.get('/admin/dashboard'),
  preview: {
    home: (includeDrafts = true) => api.get('/admin/preview/home', { params: { include_drafts: includeDrafts } }),
  },
  phase0: {
    checks: (verifyFeeds = true) => api.get('/admin/phase0/checks', { params: { verify_feeds: verifyFeeds } }),
    bootstrapPilot: (forceUpdate = false) => api.post('/admin/rss-sources/bootstrap-pilot', null, { params: { force_update: forceUpdate } }),
    fetchPilot: () => api.post('/admin/rss-sources/fetch-pilot'),
  },
  // Articles
  articles: {
    list: (params) => api.get('/admin/articles', { params }),
    create: (data) => api.post('/admin/articles', data),
    update: (id, data) => api.put(`/admin/articles/${id}`, data),
    delete: (id) => api.delete(`/admin/articles/${id}`),
  },
  // Categories
  categories: {
    list: () => api.get('/admin/categories'),
    create: (data) => api.post('/admin/categories', data),
    update: (id, data) => api.put(`/admin/categories/${id}`, data),
    delete: (id) => api.delete(`/admin/categories/${id}`),
  },
  // Subcategories
  subcategories: {
    list: () => api.get('/admin/subcategories'),
    create: (data) => api.post('/admin/subcategories', data),
    update: (id, data) => api.put(`/admin/subcategories/${id}`, data),
    delete: (id) => api.delete(`/admin/subcategories/${id}`),
  },
  // Plans
  plans: {
    list: () => api.get('/admin/plans'),
    create: (data) => api.post('/admin/plans', data),
    update: (id, data) => api.put(`/admin/plans/${id}`, data),
    delete: (id) => api.delete(`/admin/plans/${id}`),
  },
  // Users
  users: {
    list: (params) => api.get('/admin/users', { params }),
    updateRole: (id, role) => api.put(`/admin/users/${id}/role?role=${role}`),
  },
  // Comments
  comments: {
    list: (params) => api.get('/admin/comments', { params }),
    update: (id, status) => api.put(`/admin/comments/${id}?status=${status}`),
    delete: (id) => api.delete(`/admin/comments/${id}`),
  },
  // Partners
  partners: {
    list: () => api.get('/admin/partners'),
    create: (data) => api.post('/admin/partners', data),
    update: (id, data) => api.put(`/admin/partners/${id}`, data),
    delete: (id) => api.delete(`/admin/partners/${id}`),
    media: {
      list: (partnerId) => api.get(`/admin/partners/${partnerId}/media`),
      create: (partnerId, data) => api.post(`/admin/partners/${partnerId}/media`, data),
      upload: (partnerId, formData) => api.post(`/admin/partners/${partnerId}/media/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
      delete: (partnerId, mediaId) => api.delete(`/admin/partners/${partnerId}/media/${mediaId}`),
    },
  },
  // Banners
  banners: {
    list: () => api.get('/admin/banners'),
    create: (data) => api.post('/admin/banners', data),
    update: (id, data) => api.put(`/admin/banners/${id}`, data),
    delete: (id) => api.delete(`/admin/banners/${id}`),
  },
  // Radio
  radio: {
    streams: () => api.get('/admin/radio/streams'),
    createStream: (data) => clearRadioCacheAfter(api.post('/admin/radio/streams', data)),
    updateStream: (id, data) => clearRadioCacheAfter(api.put(`/admin/radio/streams/${id}`, data)),
    programs: () => api.get('/admin/radio/programs'),
    createProgram: (data) => clearRadioCacheAfter(api.post('/admin/radio/programs', data)),
    updateProgram: (id, data) => clearRadioCacheAfter(api.put(`/admin/radio/programs/${id}`, data)),
    deleteProgram: (id) => clearRadioCacheAfter(api.delete(`/admin/radio/programs/${id}`)),
    episodes: () => api.get('/admin/radio/episodes'),
    createEpisode: (data) => clearRadioCacheAfter(api.post('/admin/radio/episodes', data)),
    updateEpisode: (id, data) => clearRadioCacheAfter(api.put(`/admin/radio/episodes/${id}`, data)),
    deleteEpisode: (id) => clearRadioCacheAfter(api.delete(`/admin/radio/episodes/${id}`)),
  },
  // Polls
  polls: {
    list: () => api.get('/admin/polls'),
    create: (data) => api.post('/admin/polls', data),
    update: (id, data) => api.put(`/admin/polls/${id}`, data),
  },
  // Settings
  settings: {
    list: () => api.get('/admin/settings'),
    upsert: (data) => api.post('/admin/settings', data),
  },
  stripe: {
    status: () => api.get('/admin/stripe/status'),
  },
  // Sections
  sections: {
    get: () => api.get('/admin/sections'),
    update: (data) => api.put('/admin/sections', data),
  },
  // Newsletter
  newsletters: () => api.get('/admin/newsletters'),
  contacts: () => api.get('/admin/contacts'),
  citizenReports: {
    list: () => api.get('/admin/citizen-reports'),
    update: (id, status) => api.put(`/admin/citizen-reports/${id}?status=${status}`),
  },
  payments: (params) => api.get('/admin/payments', { params }),
  subscribers: () => api.get('/admin/subscribers'),
  // AI
  ai: {
    suggest: (data) => api.post('/admin/ai/suggest', data),
  },
};

export default api;
